import React, { useState, useEffect } from "react";
import { planToDot, isValidDot } from "./plan-dot";
import { Typography, Spin, Alert } from "antd";

// Simple validator function for DOT syntax
const validateDotSyntax = dot => {
  try {
    if (!dot) return { valid: false, error: "Empty DOT data" };

    // Basic structure checks
    if (!dot.trim().startsWith("digraph")) {
      return {
        valid: false,
        error: 'DOT syntax error: Must start with "digraph"',
      };
    }

    if (!dot.includes("{") || !dot.includes("}")) {
      return { valid: false, error: "DOT syntax error: Missing { or }" };
    }

    // Check for unbalanced quotes
    let inQuotes = false;
    let quoteChar = null;
    let line = 1;

    for (let i = 0; i < dot.length; i++) {
      const char = dot[i];

      if (char === "\n") {
        line++;
        continue;
      }

      if (char === '"' && (i === 0 || dot[i - 1] !== "\\")) {
        if (inQuotes && quoteChar === '"') {
          inQuotes = false;
          quoteChar = null;
        } else if (!inQuotes) {
          inQuotes = true;
          quoteChar = '"';
        }
      }
    }

    if (inQuotes) {
      return {
        valid: false,
        error: `DOT syntax error: Unbalanced quotes - missing closing quote`,
      };
    }

    return { valid: true };
  } catch (err) {
    return { valid: false, error: `DOT validation error: ${err.message}` };
  }
};

const PlanGraphViz = ({ planData, memData }) => {
  const [loading, setLoading] = useState(true);
  const [dot, setDot] = useState("");
  const [error, setError] = useState(null);
  const [iframeUrl, setIframeUrl] = useState("");

  // Convert plan data to DOT format
  useEffect(() => {
    if (!planData) {
      setLoading(false);
      setDot("");
      return;
    }

    console.log(
      "PlanGraphViz received memory data:",
      memData ? `${memData.length} bytes` : "none",
    );

    try {
      setLoading(true);
      const dotData = planToDot(planData, memData);

      // Validate DOT syntax
      const validation = validateDotSyntax(dotData);
      if (!validation.valid) {
        setError(validation.error);
        setDot(dotData);
        setLoading(false);
        return;
      }

      setDot(dotData);
      setError(null);

      // Create iframe URL for GraphvizOnline
      try {
        const encodedDot = encodeURIComponent(dotData);
        setIframeUrl(
          `https://dreampuf.github.io/GraphvizOnline/#${encodedDot}`,
        );
      } catch (encodeErr) {
        setError(
          `Error encoding DOT data: ${encodeErr.message}. The DOT data might be too large.`,
        );
      }

      setLoading(false);
    } catch (err) {
      console.error("Error converting plan to DOT:", err);
      setError(`Error generating GraphViz diagram: ${err.message}`);
      setLoading(false);
    }
  }, [planData, memData]);

  return (
    <div
      style={{
        width: "100%",
        height: "100%",
        position: "relative",
        overflow: "hidden",
      }}
    >
      {loading && (
        <div
          style={{
            position: "absolute",
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            backgroundColor: "rgba(255, 255, 255, 0.7)",
            zIndex: 10,
          }}
        >
          <Spin size="large" />
        </div>
      )}

      {error && (
        <Alert
          type="error"
          message={error}
          style={{ margin: 16 }}
          showIcon
        />
      )}

      {!dot && !loading && !error && (
        <Typography.Text style={{ display: "block", padding: 16, textAlign: "center" }}>
          No plan data available for visualization
        </Typography.Text>
      )}

      {dot && !error && (
        <div style={{ width: "100%", height: "100%" }}>
          <iframe
            src={iframeUrl}
            style={{ width: "100%", height: "100%", border: "none" }}
            title="GraphViz Visualization"
          />
        </div>
      )}
    </div>
  );
};

export default PlanGraphViz;
